/**
 * "Talk to Hero" voice widget — state machine hook.
 *
 * GitHub issue #555 ("P0 Bug: Voice AI widget ('Talk to Hero') failing —
 * 'Something went wrong' for new leads"). This hook is the actual fix:
 * every failure mode the ticket asks to distinguish gets its own state +
 * message, instead of one generic catch-all:
 *
 *   - malformed email       -> `invalid-email` ("Please enter a valid email address")
 *   - WIDGET_MAX_CALLS_PER_EMAIL exceeded -> `rate-limited` ("... reached
 *     the maximum number of calls ...")
 *   - voice WebSocket/ngrok tunnel down/timeout -> `error` with a
 *     specific, actionable message ("Voice service unavailable, please
 *     try again shortly") + a fire-and-forget report to
 *     `POST /api/v1/widget/connection-error` so it lands in Sentry
 *     server-side (`apps/workers/src/api/widget.ts`).
 *   - anything else truly unexpected (validate-email 500, a malformed
 *     JSON response, etc.) -> `error` with the ticket's own generic copy
 *     ("Something went wrong. Please try again.") — this is the *only*
 *     path that string is still allowed to reach, reserved for genuinely
 *     unknown failures rather than every failure.
 *
 * Deliberately framework-light (no TanStack Query / `useApiFetch()`):
 * `HowItWorks.tsx` is a statically-generated marketing page rendered
 * *outside* `ConfigProvider`/`AuthProvider` (see
 * `apps/web/src/app/router.tsx`'s module doc-comment on the two route
 * groups) — `useApiFetch()` would throw ("must be used within
 * ConfigProvider") if called from here. Instead this hook lazily resolves
 * the API base URL itself, on demand (only once a user actually opens the
 * widget and submits an email — never on page load), via
 * `@/lib/config/runtimeConfig`'s already-existing, provider-independent
 * `loadRuntimeConfigSafe()`/`getMinimalFallbackConfig()`. This keeps the
 * page's *initial* render free of any client-side API call (Requirement
 * #7's "no client-side API calls ... in these static pages", same as
 * every other marketing page), while still letting the widget work.
 */
import { useCallback, useRef, useState } from 'react';

import { apiFetch, ApiError } from '@/lib/api/fetchClient';
import { loadRuntimeConfigSafe, getMinimalFallbackConfig } from '@/lib/config/runtimeConfig';
import { isLikelyValidEmail } from '@/lib/validation/widget';
import { getVapiWidgetConfig, isVapiWidgetConfigured } from '@/lib/widget/vapiConfig';
import {
  connectVoiceCall,
  VoiceConnectionError,
  type VoiceConnectionHandle,
} from '@/lib/widget/voiceConnection';

export type TalkToHeroWidgetState =
  | { status: 'idle' }
  | { status: 'validating' }
  | { status: 'invalid-email'; message: string }
  | { status: 'rate-limited'; message: string }
  | { status: 'connecting' }
  | { status: 'active' }
  | { status: 'error'; message: string };

export const WIDGET_GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';
export const WIDGET_VOICE_UNAVAILABLE_MESSAGE = 'Voice service unavailable, please try again shortly.';
export const WIDGET_INVALID_EMAIL_MESSAGE = 'Please enter a valid email address.';

interface ValidateEmailResponse {
  ok: true;
  email: string;
  remainingCalls: number;
}

async function resolveApiBaseUrl(): Promise<string> {
  const result = await loadRuntimeConfigSafe();
  if (result.success) return result.config.apiBaseUrl;
  return getMinimalFallbackConfig().apiBaseUrl;
}

/** Fire-and-forget report of a voice-connection failure to the backend
 * (which forwards it to Sentry — see `apps/workers/src/api/widget.ts`).
 * Never throws / never awaited by the caller past this function's own
 * internal try/catch, so a failing report call can never itself surface
 * as a second user-facing error. */
async function reportConnectionError(apiBaseUrl: string, email: string, reason: string, message: string): Promise<void> {
  try {
    await apiFetch(apiBaseUrl, '/v1/widget/connection-error', {
      method: 'POST',
      json: { email, reason, message },
    });
  } catch {
    // Best-effort telemetry only — swallow.
  }
}

export interface UseTalkToHeroWidgetResult {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  state: TalkToHeroWidgetState;
  submitEmail: (email: string) => Promise<void>;
  reset: () => void;
}

export function useTalkToHeroWidget(): UseTalkToHeroWidgetResult {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<TalkToHeroWidgetState>({ status: 'idle' });
  const callHandleRef = useRef<VoiceConnectionHandle | null>(null);

  const open = useCallback(() => {
    setIsOpen(true);
    setState({ status: 'idle' });
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    callHandleRef.current?.close();
    callHandleRef.current = null;
    setState({ status: 'idle' });
  }, []);

  const reset = useCallback(() => {
    setState({ status: 'idle' });
  }, []);

  const submitEmail = useCallback(async (rawEmail: string) => {
    // Client-side pre-check: gives an instant inline message for the
    // obviously-empty case without waiting on a round-trip, but does NOT
    // replace the server call below — `POST /api/v1/widget/validate-email`
    // is still hit for any non-empty input so the server's Zod
    // validation + rate limiter are always the source of truth (GH #555
    // security case: "validate-email enforces server-side rate limiting").
    const trimmed = rawEmail.trim();
    if (trimmed.length === 0 || !isLikelyValidEmail(trimmed)) {
      // Still worth noting: this short-circuit is a UX nicety, not a
      // security boundary — a request that somehow bypassed this (e.g. a
      // scripted client calling the API directly) is still rejected
      // server-side by the same Zod schema in
      // `apps/workers/src/api/widget.ts` (GH #555 qa case 3).
      setState({ status: 'invalid-email', message: WIDGET_INVALID_EMAIL_MESSAGE });
      return;
    }

    setState({ status: 'validating' });

    let apiBaseUrl: string;
    try {
      apiBaseUrl = await resolveApiBaseUrl();
    } catch {
      setState({ status: 'error', message: WIDGET_GENERIC_ERROR_MESSAGE });
      return;
    }

    let response: ValidateEmailResponse;
    try {
      response = await apiFetch<ValidateEmailResponse>(apiBaseUrl, '/v1/widget/validate-email', {
        method: 'POST',
        json: { email: trimmed },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 400) {
          const body = err.body as { message?: string } | undefined;
          setState({ status: 'invalid-email', message: body?.message ?? WIDGET_INVALID_EMAIL_MESSAGE });
          return;
        }
        if (err.status === 429) {
          const body = err.body as { message?: string } | undefined;
          setState({
            status: 'rate-limited',
            message:
              body?.message ??
              "You've reached the maximum number of calls for this email. Please try again later.",
          });
          return;
        }
      }
      // Unknown failure (500, network error reaching our own API, etc.)
      // — the one remaining path allowed to show the ticket's original
      // generic copy, since we genuinely don't know what went wrong here.
      setState({ status: 'error', message: WIDGET_GENERIC_ERROR_MESSAGE });
      return;
    }

    // Server validated + rate-limit-cleared the email — move to the
    // "connecting" / active-conversation UI. From this point on, any
    // failure gets the specific "Voice service unavailable" message
    // (never the generic one), matching GH #555's negative test case.
    setState({ status: 'connecting' });

    // TODO (out of scope for this ticket to fully close — flagging per
    // this run's rule #4): this sandbox/CI environment has no real VAPI
    // account or voice WebSocket server (`VITE_VAPI_PUBLIC_KEY` /
    // `VITE_VAPI_ASSISTANT_ID` / `VITE_VOICE_WS_URL` are intentionally
    // left unset in `.env.example` — see that file). That means the
    // "happy path" (email validated -> live, connected conversation)
    // can only be demonstrated end-to-end once a real deploy sets these
    // three env vars to a live VAPI project + a *stable* (not a rotating
    // free-tier ngrok) voice WebSocket domain — exactly the root cause
    // this ticket's own investigation flagged ("free ngrok tunnels
    // expire and rotate URLs"). Until then, this code path correctly
    // (and honestly) reports `WIDGET_VOICE_UNAVAILABLE_MESSAGE` rather
    // than faking a successful connection.
    const vapiConfig = getVapiWidgetConfig();
    if (!isVapiWidgetConfigured(vapiConfig) || !vapiConfig.wsUrl) {
      setState({ status: 'error', message: WIDGET_VOICE_UNAVAILABLE_MESSAGE });
      void reportConnectionError(apiBaseUrl, response.email, 'ws_not_configured', 'Voice widget WS/assistant config missing');
      return;
    }

    try {
      const handle = await connectVoiceCall({ wsUrl: vapiConfig.wsUrl });
      callHandleRef.current = handle;
      setState({ status: 'active' });
    } catch (err) {
      const reason = err instanceof VoiceConnectionError ? err.reason : 'ws_error';
      const message = err instanceof Error ? err.message : 'Voice connection failed';
      setState({ status: 'error', message: WIDGET_VOICE_UNAVAILABLE_MESSAGE });
      void reportConnectionError(apiBaseUrl, response.email, reason, message);
    }
  }, []);

  return { isOpen, open, close, state, submitEmail, reset };
}
